import React from 'react';
import { compose } from 'redux';
import { LocalizedMessage, LocalizedStandardDate } from 'intl/LocalizedComponents';
import _map from 'lodash/map';
import PropTypes from 'prop-types';
// Core components
import Badge from 'components/core/Badge';
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Color
import { statusPalette } from 'components/core/colors';
// Models
import {
  isLocked, smallDesc, fileStatus, fileDesc,
} from 'models/distribution';
import { icon as deviceIcon } from 'models/device';
import { icon as rolloutIcon } from 'models/rollout';
// Custom
import { listRowSpecsShape, distributionDataShape } from 'constants/PropTypesShapes';
import { showPath } from 'distributions/navigation';
import withLoading from 'hocs/WithLoading';
import AppIcon from 'components/icons/AppIcon';
import { EditIconButton, DeleteIconButton } from 'components/buttons/IconButtons';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';

const DitributionFiles = ({ distribution }) => (
  <div className="d-flex align-items-center">
    {_map(distribution.files, (file, index) => {
      const { labelId, icon, color } = fileStatus(file);
      return (
        <Tooltip
          key={index}
          content={(
            <div>
              <div className="row m-0">
                <span>{fileDesc(file)}</span>
              </div>
              <div className="row m-0">
                <em><LocalizedMessage id={labelId} /></em>
              </div>
            </div>
            )}
        >
          <AppIcon id={icon} color={color} />
        </Tooltip>
      );
    })}
  </div>
);

DitributionFiles.propTypes = {
  distribution: distributionDataShape.isRequired,
};

const DitributionDependencies = ({ distribution }) => {
  // Icons/badges colors
  const devicesQuantityColor = distribution.devices_quantity > 0 ? statusPalette.warning : statusPalette.success;
  const rolloutsQuantityColor = distribution.rollouts_quantity > 0 ? statusPalette.warning : statusPalette.success;

  return (
    <div className="d-flex">
      <Tooltip content={<LocalizedMessage id="tooltip.distributionDevicesQtity" values={{ devicesQtity: distribution.devices_quantity }} />}>
        <div>
          <Badge content={distribution.devices_quantity} backgroundColor={devicesQuantityColor} type="small">
            <AppIcon id={deviceIcon} color={devicesQuantityColor} />
          </Badge>
        </div>
      </Tooltip>
      <Tooltip content={<LocalizedMessage id="tooltip.distributionRolloutsQtity" values={{ rolloutsQtity: distribution.rollouts_quantity }} />}>
        <div className="ml-3">
          <Badge content={distribution.rollouts_quantity} backgroundColor={rolloutsQuantityColor} type="small">
            <AppIcon id={rolloutIcon} color={rolloutsQuantityColor} />
          </Badge>
        </div>
      </Tooltip>
    </div>
  );
};

DitributionDependencies.propTypes = {
  distribution: distributionDataShape.isRequired,
};

const DistributionRow = React.forwardRef(({
  distribution, rowSpecs, handleStartLoading,
  onDelete,
}, ref) => {
  const { id: distributionId } = distribution;

  const actions = [
    <EditIconButton key="bEdit" onClick={handleStartLoading} path={showPath(distributionId)} />,
  ];
  if (!isLocked(distribution)) {
    actions.push(<DeleteIconButton key="bDelete" onClick={() => onDelete(distribution)} />);
  }

  return (
    <TableRow
      ref={ref}
      element={distribution}
      rowSpecs={rowSpecs}
      actions={actions}
    >
      <TableCell>
        <TruncatedTooltipText>{smallDesc(distribution)}</TruncatedTooltipText>
      </TableCell>
      <TableCell>
        <div className="d-flex align-items-center">
          <TruncatedTooltipText>{distribution.version}</TruncatedTooltipText>
          {distribution.disabled && (
            <div className="d-flex align-items-center ml-2">
              <AppIcon defaultColor id="disabled" />
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden-sm-down">
        <TruncatedTooltipText>
          <LocalizedStandardDate value={distribution.date} />
        </TruncatedTooltipText>
      </TableCell>
      <TableCell className="hidden-lg-down">{distribution.comment}</TableCell>
      <TableCell className="hidden-sm-down table-content-2" noPadding>
        <DitributionFiles distribution={distribution} />
      </TableCell>
      <TableCell className="hidden-sm-down table-content-2" noPadding>
        <DitributionDependencies distribution={distribution} />
      </TableCell>
    </TableRow>
  );
});

DistributionRow.displayName = 'DistributionRow';

DistributionRow.propTypes = {
  distribution: distributionDataShape.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

DistributionRow.defaultProps = {
  onDelete: null,
};

module.exports = compose(
  withLoading,
)(DistributionRow);



// WEBPACK FOOTER //
// ./routes/app/routes/distributions/assets/main/DistributionRow.js